import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBackButton from '../common/AppBackButton';
import { Modal, Button, Spinner } from 'react-bootstrap';
import {
  Download,
  Eye,
  Film,
  History,
  Link2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Shield,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'react-toastify';

import axiosInstance from '../../api/api';
import { getUserFacingError } from '../../utils/userFacingError';
import { API_ROUTES } from '../../config/api-routes';
import {
  DEFAULT_PRIVACY_SETTINGS,
  mergePrivacySettings,
  type PrivacySettings,
} from './privacySettings';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import './PrivacyPage.css';

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="privacy-page__switch">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="privacy-page__slider" />
    </label>
  );
}

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<PrivacySettings>(DEFAULT_PRIVACY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portfolioId, setPortfolioId] = useState<number | null>(null);
  const [confirmDeleteVideos, setConfirmDeleteVideos] = useState(false);
  const [confirmDeletePortfolio, setConfirmDeletePortfolio] = useState(false);
  const [deletingVideos, setDeletingVideos] = useState(false);
  const [deletingPortfolio, setDeletingPortfolio] = useState(false);
  const [downloadingData, setDownloadingData] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get('user/current-user');
        const user = (res.data?.user ?? res.data) as Record<string, unknown>;
        if (active) {
          setSettings(mergePrivacySettings(user.privacySettings));
        }
      } catch {
        /* defaults */
      }

      try {
        const portfolioRes = await axiosInstance.get(API_ROUTES.PORTFOLIO.GET_PORTFOLIO);
        const portfolio = portfolioRes.data?.portfolio ?? portfolioRes.data;
        const id = Number(portfolio?.id);
        if (active && Number.isFinite(id) && id > 0) {
          setPortfolioId(id);
        }
      } catch {
        /* no portfolio */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleDownloadData = async () => {
    setDownloadingData(true);
    try {
      const response = await axiosInstance.get(API_ROUTES.USER.EXPORT_DATA);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `skipscreen-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Your data export has been downloaded.');
    } catch (error) {
      toast.error(getUserFacingError(error, 'Failed to download your data.'));
    } finally {
      setDownloadingData(false);
    }
  };

  const handleDeleteAllVideos = async () => {
    setDeletingVideos(true);
    try {
      await axiosInstance.delete(API_ROUTES.PROMPTS.DELETE_ALL_PROMPTS);
      toast.success('All uploaded videos have been deleted.');
      setConfirmDeleteVideos(false);
    } catch (error) {
      toast.error(getUserFacingError(error, 'Failed to delete videos.'));
    } finally {
      setDeletingVideos(false);
    }
  };

  const handleDeletePortfolio = async () => {
    if (!portfolioId) {
      toast.info('You do not have a portfolio to delete.');
      setConfirmDeletePortfolio(false);
      return;
    }

    setDeletingPortfolio(true);
    try {
      await axiosInstance.delete(`${API_ROUTES.PORTFOLIO.DELETE_PORTFOLIO}/${portfolioId}`);
      toast.success('Portfolio data has been deleted.');
      setPortfolioId(null);
      setConfirmDeletePortfolio(false);
    } catch (error) {
      toast.error(getUserFacingError(error, 'Failed to delete portfolio.'));
    } finally {
      setDeletingPortfolio(false);
    }
  };

  const saveSettings = useCallback(async (next: PrivacySettings) => {
    setSaving(true);
    try {
      await axiosInstance.put(
        API_ROUTES.USER.UPDATE_USER,
        { privacySettings: next },
        { suppressErrorToast: true }
      );
      setSettings(next);
    } catch (err) {
      toast.error(
        getUserFacingError(err, 'We could not save your privacy settings. Please try again.')
      );
    } finally {
      setSaving(false);
    }
  }, []);

  const patch = (partial: Partial<PrivacySettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...partial };
      void saveSettings(next);
      return next;
    });
  };

  if (loading) {
    return <PageBusyLoader pageName="Privacy" />;
  }

  return (
    <div className="privacy-page">
      <div className="privacy-page__toolbar">
        <AppBackButton className="privacy-page__back-btn" />
      </div>

      <header className="privacy-page__head">
        <h1 className="privacy-page__title">Privacy</h1>
        <p className="privacy-page__subtitle">
          Control who can view your profile and how your information is shared.
        </p>
      </header>

      {saving ? <p className="privacy-page__saving">Saving…</p> : null}

      <section className="privacy-page__card">
        <h2 className="privacy-page__card-title">Profile visibility</h2>

        <div className="privacy-page__row">
          <Eye size={18} className="text-success" aria-hidden />
          <span className="privacy-page__row-text">
            <strong>Public profile</strong>
            <span>Anyone with your profile link can view your profile.</span>
          </span>
          <Toggle
            checked={settings.publicProfile}
            disabled={saving}
            onChange={(v) => patch({ publicProfile: v })}
          />
        </div>

        <div className="privacy-page__row">
          <Shield size={18} className="text-success" aria-hidden />
          <span className="privacy-page__row-text">
            <strong>
              Verified recruiters only
              <span className="privacy-page__chip">Recommended</span>
            </strong>
            <span>Only approved recruiters and hiring personnel can view your profile.</span>
          </span>
          <Toggle
            checked={settings.verifiedRecruitersOnly}
            disabled={saving}
            onChange={(v) => patch({ verifiedRecruitersOnly: v })}
          />
        </div>

        <div className="privacy-page__card-subsection">
          <h3 className="privacy-page__card-subtitle">Recruiter access</h3>
          <label className="privacy-page__radio-row">
            <input
              type="radio"
              name="verified-recruiter-access"
              checked={!settings.verifiedRecruiterAutoAccess}
              disabled={saving}
              onChange={() => patch({ verifiedRecruiterAutoAccess: false })}
            />
            <span>
              <strong>Verified recruiters must request Full Access</strong>
              <span className="privacy-page__notice">
                Requiring approval provides greater control but may reduce recruiter engagement
                because some recruiters may not wait for access.
              </span>
            </span>
          </label>
          <label className="privacy-page__radio-row">
            <input
              type="radio"
              name="verified-recruiter-access"
              checked={settings.verifiedRecruiterAutoAccess}
              disabled={saving}
              onChange={() => patch({ verifiedRecruiterAutoAccess: true })}
            />
            <span>
              <strong>Verified recruiters automatically receive Full Access</strong>
              <span className="privacy-page__notice">
                Automatically granting Full Access reduces friction but may allow access from any
                user with a verified company email domain.
              </span>
            </span>
          </label>
        </div>

        <div className="privacy-page__row">
          <span className="privacy-page__row-text">
            <strong>Temporary access</strong>
            <span>Profile access automatically expires after:</span>
          </span>
          <div className="privacy-page__select-wrap">
            <select
              value={settings.temporaryAccessDays}
              disabled={saving}
              onChange={(e) => patch({ temporaryAccessDays: Number(e.target.value) })}
            >
              <option value={7}>7 Days</option>
              <option value={15}>15 Days</option>
              <option value={30}>30 Days</option>
              <option value={60}>60 Days</option>
            </select>
          </div>
        </div>
      </section>

      <section className="privacy-page__card">
        <h2 className="privacy-page__card-title">Contact &amp; communication</h2>

        <div className="privacy-page__row">
          <MessageCircle size={18} className="text-success" aria-hidden />
          <span className="privacy-page__row-text">
            <strong>Allow direct messages</strong>
            <span>Allow recruiters to message you.</span>
          </span>
          <Toggle
            checked={settings.allowDirectMessages}
            disabled={saving}
            onChange={(v) => patch({ allowDirectMessages: v })}
          />
        </div>

        <div className="privacy-page__row">
          <span className="privacy-page__row-text">
            <strong>Allow questions</strong>
            <span>Allow recruiters to ask questions on your profile.</span>
          </span>
          <Toggle
            checked={settings.allowQuestions}
            disabled={saving}
            onChange={(v) => patch({ allowQuestions: v })}
          />
        </div>

        <div className="privacy-page__row">
          <span className="privacy-page__row-text">
            <strong>Show calendar availability</strong>
            <span>Allow recruiters to see your available interview times.</span>
          </span>
          <Toggle
            checked={settings.showCalendarAvailability}
            disabled={saving}
            onChange={(v) => patch({ showCalendarAvailability: v })}
          />
        </div>
      </section>

      <section className="privacy-page__card">
        <h2 className="privacy-page__card-title">Personal information visibility</h2>
        <div className="privacy-page__grid">
          {(
            [
              ['showEmail', 'Show email', Mail],
              ['showPhone', 'Show phone', Phone],
              ['showLocation', 'Show location', MapPin],
              ['showSalaryExpectations', 'Show salary', Users],
              ['showCurrentCompany', 'Show company', Users],
              ['showWorkAnniversary', 'Work Arrangements', Users],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="privacy-page__grid-item">
              <span>{label}</span>
              <Toggle
                checked={settings[key]}
                disabled={saving}
                onChange={(v) => patch({ [key]: v })}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="privacy-page__card">
        <h2 className="privacy-page__card-title">Portfolio &amp; resume</h2>

        <div className="privacy-page__row">
          <span className="privacy-page__row-text">
            <strong>Allow resume downloads</strong>
            <span>Recruiters can download your resume.</span>
          </span>
          <Toggle
            checked={settings.allowResumeDownloads}
            disabled={saving}
            onChange={(v) => patch({ allowResumeDownloads: v })}
          />
        </div>

        <div className="privacy-page__row">
          <Film size={18} className="text-success" aria-hidden />
          <span className="privacy-page__row-text">
            <strong>Allow video sharing outside SkipScreen</strong>
            <span>Recruiters can share your videos outside the platform.</span>
          </span>
          <Toggle
            checked={settings.allowVideoSharingOutside}
            disabled={saving}
            onChange={(v) => patch({ allowVideoSharingOutside: v })}
          />
        </div>

        <p className="privacy-page__notice">
          SkipScreen prevents standard downloads and direct access to video files. However, no
          online platform can fully prevent screen recording or recording with another device.
        </p>

        <div className="privacy-page__row">
          <Link2 size={18} className="text-success" aria-hidden />
          <span className="privacy-page__row-text">
            <strong>Generate protected link</strong>
            <span>Create a secure profile link that can expire automatically.</span>
          </span>
          <button
            type="button"
            className="privacy-page__btn"
            onClick={() => toast.info('Protected links are coming soon.')}
          >
            Generate Link
          </button>
        </div>
      </section>

      <section className="privacy-page__card">
        <h2 className="privacy-page__card-title">Recruiter transparency</h2>

        <div className="privacy-page__row">
          <span className="privacy-page__row-text">
            <strong>Only show interactions from verified recruiters</strong>
            <span>Hide activity from unverified or suspicious accounts.</span>
          </span>
          <Toggle
            checked={settings.onlyVerifiedRecruiterInteractions}
            disabled={saving}
            onChange={(v) => patch({ onlyVerifiedRecruiterInteractions: v })}
          />
        </div>

        <div className="privacy-page__row">
          <History size={18} className="text-success" aria-hidden />
          <span className="privacy-page__row-text">
            <strong>View access history</strong>
            <span>See who viewed your profile and what they accessed.</span>
          </span>
          <button type="button" className="privacy-page__btn" onClick={() => navigate('/activity')}>
            View History
          </button>
        </div>
      </section>

      <section className="privacy-page__card">
        <h2 className="privacy-page__card-title">Data control</h2>

        <button
          type="button"
          className="privacy-page__danger-row"
          onClick={() => void handleDownloadData()}
          disabled={downloadingData}
        >
          <Download className="privacy-page__danger-icon" size={18} aria-hidden />
          <span className="privacy-page__row-text">
            <strong>Download my data</strong>
            <span>
              {downloadingData
                ? 'Preparing your export…'
                : 'Get a JSON copy of your profile, prompts, and portfolio.'}
            </span>
          </span>
        </button>

        <button
          type="button"
          className="privacy-page__danger-row"
          onClick={() => setConfirmDeleteVideos(true)}
        >
          <Trash2 className="privacy-page__danger-icon" size={18} aria-hidden />
          <span className="privacy-page__row-text">
            <strong>Delete all uploaded videos</strong>
            <span>Permanently delete your screening prompt videos.</span>
          </span>
        </button>

        <button
          type="button"
          className="privacy-page__danger-row"
          onClick={() => setConfirmDeletePortfolio(true)}
          disabled={!portfolioId}
        >
          <Trash2 className="privacy-page__danger-icon" size={18} aria-hidden />
          <span className="privacy-page__row-text">
            <strong>Delete portfolio data</strong>
            <span>
              {portfolioId
                ? 'Permanently delete your portfolio.'
                : 'No portfolio found on your account.'}
            </span>
          </span>
        </button>
      </section>

      <Modal
        show={confirmDeleteVideos}
        onHide={() => !deletingVideos && setConfirmDeleteVideos(false)}
        centered
      >
        <Modal.Header closeButton={!deletingVideos}>
          <Modal.Title>Delete all uploaded videos?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This permanently removes every screening prompt video on your profile. This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmDeleteVideos(false)}
            disabled={deletingVideos}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleDeleteAllVideos()}
            disabled={deletingVideos}
          >
            {deletingVideos ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete all videos'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmDeletePortfolio}
        onHide={() => !deletingPortfolio && setConfirmDeletePortfolio(false)}
        centered
      >
        <Modal.Header closeButton={!deletingPortfolio}>
          <Modal.Title>Delete portfolio data?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This permanently removes your portfolio, including projects, certifications, and other
          portfolio sections. This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmDeletePortfolio(false)}
            disabled={deletingPortfolio}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void handleDeletePortfolio()}
            disabled={deletingPortfolio || !portfolioId}
          >
            {deletingPortfolio ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete portfolio'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PrivacyPage;
